"use client";

import { useState } from "react";
import { Check, ChevronDown, Upload } from "lucide-react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import UploadFile, { FileType } from "@/components/utils/uploadFile";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { BUILT_IN_FONTS } from "../lib/builtInFonts";
import { parseFontFamily } from "../lib/parseFontFamily";
import { useBrandKitFontFaces } from "../hooks/useBrandKitFontFaces";
import type { CustomFont } from "../types";

export interface FontSelection {
    /** CSS font-family name. */
    family: string;
    /** Public URL for custom fonts; null for built-ins. */
    url: string | null;
}

interface FontSelectorProps {
    /** When omitted, the selector renders without a label (e.g. for an
     * RTE toolbar). */
    label?: string;
    value: FontSelection;
    onChange: (next: FontSelection) => void;
    /** Shared list of custom fonts. Both the titles and body selectors in the
     * brand-kit modal render the same list; the lomda editor's RTE control
     * reuses the lomda's `metadata.style.customFonts`. */
    customFonts: CustomFont[];
    /** Append a newly uploaded font to the parent's custom fonts list. */
    onAddCustomFont: (font: CustomFont) => void;
    /** Override the trigger button's class — used to match the toolbar's
     * heading-picker height/width. */
    triggerClassName?: string;
    /** Where focus goes when the list closes (the RTE toolbar puts it back in
     *  the text). Defaults to the trigger. */
    onCloseAutoFocus?: (e: Event) => void;
    /** Called when a click outside closes the list. */
    onInteractOutside?: (e: Event) => void;
    /** A first item that drops the text's own font so it follows the theme
     *  again. While `selected`, the theme font's own row is checked too. */
    themeFont?: {
        label: string;
        family: string;
        selected: boolean;
        onSelect: () => void;
    };
}

/** Strip extension and replace separators so `Open-Sans.ttf` → `Open Sans`. */
function familyFromFilename(fileName: string): string {
    const noExt = fileName.replace(/\.[^/.]+$/, "");
    return noExt.replace(/[-_]+/g, " ").trim() || "Custom Font";
}

export function FontSelector({
    label,
    value,
    onChange,
    customFonts,
    onAddCustomFont,
    triggerClassName,
    onCloseAutoFocus,
    onInteractOutside,
    themeFont,
}: FontSelectorProps) {
    const { locale } = useLocale();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [showUpload, setShowUpload] = useState(false);

    // Register every kit-level custom font so previews in the dropdown render
    // in the correct face — not just the one currently selected.
    useBrandKitFontFaces([
        { family: value.family, url: value.url },
        ...customFonts.map((f) => ({ family: f.family, url: f.url })),
    ]);

    const uploadPath = user?.id
        ? `users/${user.id}/brand-kits/fonts`
        : undefined;

    const popover = (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    type="button"
                    className={cn(
                        "h-10 w-full justify-between font-normal",
                        triggerClassName
                    )}
                >
                    <span
                        className="truncate"
                        style={{ fontFamily: value.family }}
                    >
                        {value.family ||
                            t("brand-kits.font-selector.choose", locale)}
                    </span>
                    <ChevronDown className="size-4 opacity-60 shrink-0" />
                </Button>
            </PopoverTrigger>
                <PopoverContent
                    className="w-72 p-0 max-h-[22rem] flex flex-col"
                    align="start"
                    onWheel={(e) => e.stopPropagation()}
                    onCloseAutoFocus={onCloseAutoFocus}
                    onInteractOutside={onInteractOutside}
                >
                    <div className="flex-1 overflow-y-auto overscroll-contain py-1 min-h-0">
                        {themeFont && (
                            <>
                                <button
                                    type="button"
                                    className={cn(
                                        "flex w-full items-center justify-between gap-2 px-3 py-2 text-start text-sm hover:bg-muted",
                                        themeFont.selected && "bg-muted/50"
                                    )}
                                    onClick={() => {
                                        themeFont.onSelect();
                                        setOpen(false);
                                    }}
                                >
                                    <span
                                        className="truncate"
                                        style={{ fontFamily: themeFont.family }}
                                    >
                                        {themeFont.label}
                                    </span>
                                    {themeFont.selected && (
                                        <Check className="size-4 shrink-0" />
                                    )}
                                </button>
                                <div className="border-t my-1" />
                            </>
                        )}
                        {customFonts.length > 0 && (
                            <>
                                <div className="px-3 pt-1 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                                    {t(
                                        "brand-kits.font-selector.custom-fonts",
                                        locale
                                    )}
                                </div>
                                {customFonts.map((font) => {
                                    const selected =
                                        value.family === font.family &&
                                        value.url === font.url;
                                    return (
                                        <button
                                            type="button"
                                            key={`custom-${font.url}`}
                                            className={cn(
                                                "flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-muted",
                                                selected && "bg-muted/50"
                                            )}
                                            onClick={() => {
                                                onChange({
                                                    family: font.family,
                                                    url: font.url,
                                                });
                                                setOpen(false);
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontFamily: font.family,
                                                }}
                                            >
                                                {font.family}
                                            </span>
                                            {selected && (
                                                <Check className="size-4" />
                                            )}
                                        </button>
                                    );
                                })}
                                <div className="border-t my-1" />
                            </>
                        )}
                        <div className="px-3 pt-1 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                            {t("brand-kits.font-control.builtin", locale)}
                        </div>
                        {BUILT_IN_FONTS.map((font) => {
                            const selected =
                                !value.url &&
                                value.family === font;
                            return (
                                <button
                                    type="button"
                                    key={font}
                                    className={cn(
                                        "flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-muted",
                                        selected && "bg-muted/50"
                                    )}
                                    onClick={() => {
                                        onChange({
                                            family: font,
                                            url: null,
                                        });
                                        setOpen(false);
                                    }}
                                >
                                    <span style={{ fontFamily: font }}>
                                        {font}
                                    </span>
                                    {selected && <Check className="size-4" />}
                                </button>
                            );
                        })}
                    </div>
                    <div className="border-t p-2 shrink-0">
                        {!showUpload ? (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="w-full justify-start gap-2"
                                onClick={() => setShowUpload(true)}
                            >
                                <Upload className="size-4" />
                                {t(
                                    "brand-kits.font-selector.upload-custom",
                                    locale
                                )}
                            </Button>
                        ) : uploadPath ? (
                            <UploadFile
                                options={{
                                    allowedFileTypes: [FileType.Font],
                                    fileUploadPath: uploadPath,
                                    maxFileSize: 2,
                                    className: "h-20 text-xs",
                                    title: {
                                        text: t(
                                            "brand-kits.font-selector.upload.title",
                                            locale
                                        ),
                                        keyName:
                                            "brand-kits.font-selector.upload.title",
                                    },
                                    subtitle: {
                                        text: t(
                                            "brand-kits.font-selector.upload.subtitle",
                                            locale
                                        ),
                                        keyName:
                                            "brand-kits.font-selector.upload.subtitle",
                                    },
                                }}
                                onUploadFinished={async (uploaded) => {
                                    // Parse the actual font's `name` table to
                                    // get the real family. Fall back to a
                                    // filename guess on any failure (network,
                                    // non-SFNT input, malformed file).
                                    let family = familyFromFilename(
                                        uploaded.fileName
                                    );
                                    try {
                                        const res = await fetch(
                                            uploaded.fileUrl
                                        );
                                        if (res.ok) {
                                            const buf = await res.arrayBuffer();
                                            const parsed = parseFontFamily(buf);
                                            if (parsed) family = parsed;
                                        }
                                    } catch {
                                        // keep filename fallback
                                    }
                                    const next = {
                                        family,
                                        url: uploaded.fileUrl,
                                    };
                                    onAddCustomFont(next);
                                    onChange(next);
                                    setShowUpload(false);
                                    setOpen(false);
                                }}
                            />
                        ) : (
                            <p className="text-xs text-muted-foreground px-2">
                                {t(
                                    "brand-kits.font-selector.sign-in-required",
                                    locale
                                )}
                            </p>
                        )}
                    </div>
            </PopoverContent>
        </Popover>
    );

    if (!label) return popover;
    return (
        <div className="flex flex-col gap-1">
            <Label className="text-sm">{label}</Label>
            {popover}
        </div>
    );
}
